import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Grid, Chip, Divider, Avatar, Paper } from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon, People as PeopleIcon, AccountBalance as AccountBalanceIcon, AttachMoney as AttachMoneyIcon, Security as SecurityIcon, Timeline as TimelineIcon, CheckCircle as CheckCircleIcon, HourglassEmpty as HourglassEmptyIcon } from '@mui/icons-material';
interface AccordionTransactionModalProps {
  transactionId: string;
  isOpen: boolean;
  onClose: () => void;
  transaction?: any; // Optional: pass full transaction object to avoid localStorage lookup
}
interface TransactionDetail {
  id: string;
  status: 'Pending' | 'In-Process' | 'Completed' | 'Rejected' | 'Cancelled';
  overview: {
    transactionId: string;
    date: string;
    amount: number;
    currency: string;
    exchangeRate: number;
    payoutAmount: number;
    payoutCurrency: string;
    status: string;
  };
  parties: {
    sender: {
      name: string;
      country: string;
      city: string;
      idType: string;
      idNumber: string;
      mobile: string;
    };
    receiver: {
      name: string;
      country: string;
      city: string;
      mobile: string;
    };
  };
  accounts: {
    bankName: string;
    bankBranch: string;
    accountNumber: string;
    accountTitle: string;
  };
  amounts: {
    sendAmount: number;
    sendCurrency: string;
    payoutAmount: number;
    payoutCurrency: string;
    purpose: string;
    purposeCode: string;
  };
  screening: {
    status: 'Passed' | 'Failed' | 'Pending';
  };
  timeline: Array<{
    title: string;
    description: string;
    timestamp: string;
    status: 'completed' | 'current' | 'pending' | 'error';
  }>;
}
const AccordionTransactionModal: React.FC<AccordionTransactionModalProps> = ({
  transactionId,
  isOpen,
  onClose,
  transaction: passedTransaction
}) => {
  const [open, setOpen] = useState(isOpen);
  const [loading, setLoading] = useState(false);
  const [transactionData, setTransactionData] = useState<TransactionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('overview');
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  // Generate demo transaction details from table data
  const generateDemoTransactionDetails = (tableTransaction: any): TransactionDetail => {
    const status = tableTransaction.status || 'Pending';
    const currentDate = new Date().toISOString().split('T')[0];
    // Purpose mapping
    const purposeMap: {
      [key: string]: {
        full: string;
        code: string;
      };
    } = {
      'Family Support': {
        full: 'Family Support',
        code: 'FAM'
      },
      Education: {
        full: 'Education',
        code: 'EDU'
      },
      Business: {
        full: 'Business',
        code: 'BUS'
      },
      'Medical Treatment': {
        full: 'Medical Treatment',
        code: 'MED'
      },
      'Business Investment': {
        full: 'Business Investment',
        code: 'BUS'
      },
      Gift: {
        full: 'Gift',
        code: 'GFT'
      }
    };
    const defaultPurpose = purposeMap['Family Support'];
    // Determine screening status based on transaction status
    const getScreeningStatus = (): 'Passed' | 'Failed' | 'Pending' => {
      if (status === 'Rejected' || status === 'Cancelled') {
        return 'Failed';
      } else if (status === 'Pending') {
        return 'Pending';
      } else {
        return 'Passed';
      }
    };
    // Use existing timeline if available, otherwise generate one
    const getTimeline = () => {
      // If transaction has a timeline property, use it
      if (tableTransaction.timeline && Array.isArray(tableTransaction.timeline)) {
        return tableTransaction.timeline;
      }
      // Otherwise generate timeline based on status
      return generateTimelineFromStatus(status, tableTransaction.date || currentDate);
    };
    // Generate timeline based on status (fallback)
    const generateTimelineFromStatus = (status: string, date: string) => {
      const baseTimeline = [{
        title: 'Transaction Initiated',
        description: 'Transaction created by teller',
        timestamp: tableTransaction.date || currentDate,
        status: 'completed' as const
      }];
      if (status === 'Completed') {
        return [...baseTimeline, {
          title: 'Compliance Check',
          description: 'All checks passed',
          timestamp: tableTransaction.date || currentDate,
          status: 'completed' as const
        }, {
          title: 'Payment Processing',
          description: 'Payment authorized and processed',
          timestamp: tableTransaction.date || currentDate,
          status: 'completed' as const
        }, {
          title: 'Transaction Complete',
          description: 'Funds delivered to receiver',
          timestamp: tableTransaction.date || currentDate,
          status: 'completed' as const
        }];
      } else if (status === 'In-Process') {
        return [...baseTimeline, {
          title: 'Compliance Check',
          description: 'All checks passed',
          timestamp: tableTransaction.date || currentDate,
          status: 'completed' as const
        }, {
          title: 'Payment Processing',
          description: 'Payment being processed',
          timestamp: 'In Progress',
          status: 'current' as const
        }, {
          title: 'Transaction Complete',
          description: 'Pending completion',
          timestamp: 'Pending',
          status: 'pending' as const
        }];
      } else if (status === 'Rejected' || status === 'Cancelled') {
        return [...baseTimeline, {
          title: 'Compliance Check',
          description: 'Transaction flagged',
          timestamp: tableTransaction.date || currentDate,
          status: 'error' as const
        }, {
          title: 'Transaction Rejected',
          description: `Transaction ${status.toLowerCase()}`,
          timestamp: tableTransaction.date || currentDate,
          status: 'error' as const
        }];
      } else {
        // Pending
        return [...baseTimeline, {
          title: 'Compliance Check',
          description: 'Awaiting compliance review',
          timestamp: 'Pending',
          status: 'current' as const
        }, {
          title: 'Payment Processing',
          description: 'Pending payment authorization',
          timestamp: 'Pending',
          status: 'pending' as const
        }, {
          title: 'Transaction Complete',
          description: 'Funds delivered to receiver',
          timestamp: 'Pending',
          status: 'pending' as const
        }];
      }
    };
    return {
      id: tableTransaction.id,
      status: status,
      overview: {
        transactionId: tableTransaction.id,
        date: tableTransaction.date || currentDate,
        amount: tableTransaction.amount || 0,
        currency: tableTransaction.currency || 'USD',
        exchangeRate: tableTransaction.exchangeRate || 1550,
        payoutAmount: tableTransaction.payoutAmount || 0,
        payoutCurrency: tableTransaction.payoutCurrency || 'NGN',
        status: status
      },
      parties: {
        sender: {
          name: tableTransaction.senderName || 'Unknown Sender',
          country: tableTransaction.country || 'Nigeria',
          city: tableTransaction.senderCity || 'Lagos',
          idType: tableTransaction.idType || 'Passport',
          idNumber: tableTransaction.idNumber || 'NG1234567',
          mobile: tableTransaction.senderMobile || '+2348012345678'
        },
        receiver: {
          name: tableTransaction.receiverName || 'Unknown Receiver',
          country: tableTransaction.receiverCountry || 'Ghana',
          city: tableTransaction.receiverCity || 'Accra',
          mobile: tableTransaction.receiverMobile || '+233701234567'
        }
      },
      accounts: {
        bankName: tableTransaction.bankName || 'GTBank',
        bankBranch: tableTransaction.bankBranch || 'Victoria Island Branch',
        accountNumber: tableTransaction.accountNumber || 'NG0058GTBK00098765432',
        accountTitle: tableTransaction.receiverName || 'Unknown'
      },
      amounts: {
        sendAmount: tableTransaction.amount || 0,
        sendCurrency: tableTransaction.currency || 'USD',
        payoutAmount: tableTransaction.payoutAmount || 0,
        payoutCurrency: tableTransaction.payoutCurrency || 'NGN',
        purpose: defaultPurpose.full,
        purposeCode: defaultPurpose.code
      },
      screening: {
        status: getScreeningStatus()
      },
      timeline: getTimeline()
    };
  };
  useEffect(() => {
    const loadTransaction = async () => {
      setLoading(true);
      try {
        // If transaction data was passed directly, use it
        if (passedTransaction) {
          const fullTransactionData = generateDemoTransactionDetails(passedTransaction);
          setTransactionData(fullTransactionData);
          setLoading(false);
          return;
        }
        // First, try to load from localStorage
        const stored = localStorage.getItem('initiatedTransactions');
        if (stored) {
          const initiatedTransactions = JSON.parse(stored);
          const foundTransaction = initiatedTransactions.find((t: any) => t.id === transactionId);
          if (foundTransaction) {
            const fullTransactionData = generateDemoTransactionDetails(foundTransaction);
            setTransactionData(fullTransactionData);
            setLoading(false);
            return;
          }
        }
        // If not found in localStorage, try to find in the table transactions
        const tableTransactions = JSON.parse(localStorage.getItem('tableTransactions') || '[]');
        const tableTransaction = tableTransactions.find((t: any) => t.id === transactionId);
        if (tableTransaction) {
          const demoData = generateDemoTransactionDetails(tableTransaction);
          setTransactionData(demoData);
        } else {
          setError('Transaction not found');
        }
      } catch (err) {
        console.error('Error loading transaction:', err);
        setError('Failed to load transaction details');
      } finally {
        setLoading(false);
      }
    };
    if (open && transactionId) {
      loadTransaction();
    }
  }, [open, transactionId, passedTransaction]);
  if (!open) return null;
  if (loading) {
    return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-id="element-478">
        <DialogContent data-id="element-479">
          <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 5
        }} data-id="element-480">
            <Typography data-id="element-481">Loading transaction details...</Typography>
          </Box>
        </DialogContent>
      </Dialog>;
  }
  if (error) {
    return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-id="element-482">
        <DialogTitle data-id="element-483">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-484">
            <Typography variant="h6" data-id="element-485">Error</Typography>
            <IconButton onClick={onClose} size="small" data-id="element-486">
              <CloseIcon data-id="element-487" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-488">
          <Box sx={{
          py: 5,
          textAlign: 'center'
        }} data-id="element-489">
            <Typography color="text.secondary" data-id="element-490">{error}</Typography>
          </Box>
        </DialogContent>
      </Dialog>;
  }
  if (!transactionData) {
    return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-id="element-491">
        <DialogTitle data-id="element-492">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-493">
            <Typography variant="h6" data-id="element-494">Transaction Not Found</Typography>
            <IconButton onClick={onClose} size="small" data-id="element-495">
              <CloseIcon data-id="element-496" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-497">
          <Box sx={{
          py: 5,
          textAlign: 'center'
        }} data-id="element-498">
            <Typography color="text.secondary" data-id="element-499">
              Transaction {transactionId} could not be found.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>;
  }
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In-Process':
        return 'info';
      case 'Pending':
        return 'warning';
      case 'Rejected':
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-id="element-500">
      <DialogTitle data-id="element-501">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-502">
          <Box data-id="element-503">
            <Typography variant="h6" data-id="element-504">Transaction Details</Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-505">
              {transactionData?.overview.transactionId}
            </Typography>
          </Box>
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }} data-id="element-506">
            <Chip label={transactionData?.status} color={getStatusColor(transactionData?.status || 'Pending')} size="small" data-id="element-507" />
            <IconButton onClick={onClose} size="small" data-id="element-508">
              <CloseIcon data-id="element-509" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers data-id="element-510">
        <Box sx={{
        width: '100%'
      }} data-id="element-511">
          {/* Overview Accordion */}
          <Accordion expanded={expanded === 'overview'} onChange={handleChange('overview')} sx={{
          borderRadius: 3,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-512">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-514" />} data-id="element-513">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-515">
                <Avatar sx={{
                bgcolor: 'primary.light',
                width: 32,
                height: 32
              }} data-id="element-516">
                  <InfoIcon fontSize="small" data-id="element-517" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-518">
                  Overview
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-519">
              <Grid container spacing={2} data-id="element-520">
                <Grid item xs={6} data-id="element-521">
                  <Typography variant="caption" color="text.secondary" data-id="element-522">
                    Transaction ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-523">
                    {transactionData.overview.transactionId}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-524">
                  <Typography variant="caption" color="text.secondary" data-id="element-525">
                    Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-526">
                    {transactionData.overview.date}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-527">
                  <Typography variant="caption" color="text.secondary" data-id="element-528">
                    Send Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-529">
                    {formatCurrency(transactionData.overview.amount, transactionData.overview.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-530">
                  <Typography variant="caption" color="text.secondary" data-id="element-531">
                    Payout Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-532">
                    {formatCurrency(transactionData.overview.payoutAmount, transactionData.overview.payoutCurrency)}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-533">
                  <Typography variant="caption" color="text.secondary" data-id="element-534">
                    Exchange Rate
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-535">
                    {transactionData.overview.exchangeRate}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-536">
                  <Typography variant="caption" color="text.secondary" data-id="element-537">
                    Status
                  </Typography>
                  <Box data-id="element-538">
                    <Chip label={transactionData.overview.status} color={getStatusColor(transactionData.overview.status)} size="small" data-id="element-539" />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Parties Accordion */}
          <Accordion expanded={expanded === 'parties'} onChange={handleChange('parties')} sx={{
          borderRadius: 3,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-540">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-542" />} data-id="element-541">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-543">
                <Avatar sx={{
                bgcolor: 'secondary.light',
                width: 32,
                height: 32
              }} data-id="element-544">
                  <PeopleIcon fontSize="small" data-id="element-545" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-546">
                  Parties
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-547">
              <Grid container spacing={3} data-id="element-548">
                <Grid item xs={12} md={6} data-id="element-549">
                  <Paper elevation={0} sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }} data-id="element-550">
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom data-id="element-551">
                      Sender
                    </Typography>
                    <Divider sx={{
                    mb: 1.5
                  }} data-id="element-552" />
                    <Grid container spacing={1.5} data-id="element-553">
                      <Grid item xs={12} data-id="element-554">
                        <Typography variant="caption" color="text.secondary" data-id="element-555">
                          Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-556">
                          {transactionData.parties.sender.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-557">
                        <Typography variant="caption" color="text.secondary" data-id="element-558">
                          Country
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-559">
                          {transactionData.parties.sender.country}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-560">
                        <Typography variant="caption" color="text.secondary" data-id="element-561">
                          City
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-562">
                          {transactionData.parties.sender.city}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-563">
                        <Typography variant="caption" color="text.secondary" data-id="element-564">
                          ID Type
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-565">
                          {transactionData.parties.sender.idType}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-566">
                        <Typography variant="caption" color="text.secondary" data-id="element-567">
                          ID Number
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-568">
                          {transactionData.parties.sender.idNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-569">
                        <Typography variant="caption" color="text.secondary" data-id="element-570">
                          Mobile
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-571">
                          {transactionData.parties.sender.mobile}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} data-id="element-572">
                  <Paper elevation={0} sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }} data-id="element-573">
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom data-id="element-574">
                      Receiver
                    </Typography>
                    <Divider sx={{
                    mb: 1.5
                  }} data-id="element-575" />
                    <Grid container spacing={1.5} data-id="element-576">
                      <Grid item xs={12} data-id="element-577">
                        <Typography variant="caption" color="text.secondary" data-id="element-578">
                          Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-579">
                          {transactionData.parties.receiver.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-580">
                        <Typography variant="caption" color="text.secondary" data-id="element-581">
                          Country
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-582">
                          {transactionData.parties.receiver.country}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-583">
                        <Typography variant="caption" color="text.secondary" data-id="element-584">
                          City
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-585">
                          {transactionData.parties.receiver.city}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-586">
                        <Typography variant="caption" color="text.secondary" data-id="element-587">
                          Mobile
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-588">
                          {transactionData.parties.receiver.mobile}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Accounts Accordion */}
          <Accordion expanded={expanded === 'accounts'} onChange={handleChange('accounts')} sx={{
          borderRadius: 3,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-589">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-591" />} data-id="element-590">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-592">
                <Avatar sx={{
                bgcolor: 'info.light',
                width: 32,
                height: 32
              }} data-id="element-593">
                  <AccountBalanceIcon fontSize="small" data-id="element-594" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-595">
                  Accounts
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-596">
              <Grid container spacing={2} data-id="element-597">
                <Grid item xs={6} data-id="element-598">
                  <Typography variant="caption" color="text.secondary" data-id="element-599">
                    Bank Name
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-600">
                    {transactionData.accounts.bankName}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-601">
                  <Typography variant="caption" color="text.secondary" data-id="element-602">
                    Bank Branch
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-603">
                    {transactionData.accounts.bankBranch}
                  </Typography>
                </Grid>
                <Grid item xs={12} data-id="element-604">
                  <Typography variant="caption" color="text.secondary" data-id="element-605">
                    Account Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-606">
                    {transactionData.accounts.accountNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} data-id="element-607">
                  <Typography variant="caption" color="text.secondary" data-id="element-608">
                    Account Title
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-609">
                    {transactionData.accounts.accountTitle}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Amounts & FX Accordion */}
          <Accordion expanded={expanded === 'amounts'} onChange={handleChange('amounts')} sx={{
          borderRadius: 3,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-610">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-612" />} data-id="element-611">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-613">
                <Avatar sx={{
                bgcolor: 'success.light',
                width: 32,
                height: 32
              }} data-id="element-614">
                  <AttachMoneyIcon fontSize="small" data-id="element-615" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-616">
                  Amounts & FX
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-617">
              <Grid container spacing={2} data-id="element-618">
                <Grid item xs={6} data-id="element-619">
                  <Typography variant="caption" color="text.secondary" data-id="element-620">
                    Send Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-621">
                    {formatCurrency(transactionData?.amounts.sendAmount || 0, transactionData?.amounts.sendCurrency || 'USD')}
                  </Typography>
                </Grid>
                <Grid item xs={6} data-id="element-622">
                  <Typography variant="caption" color="text.secondary" data-id="element-623">
                    Payout Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-624">
                    {formatCurrency(transactionData?.amounts.payoutAmount || 0, transactionData?.amounts.payoutCurrency || 'PKR')}
                  </Typography>
                </Grid>
                <Grid item xs={12} data-id="element-625">
                  <Typography variant="caption" color="text.secondary" data-id="element-626">
                    Purpose
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-id="element-627">
                    {transactionData?.amounts.purpose} (
                    {transactionData?.amounts.purposeCode})
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Screening Summary Accordion */}
          <Accordion expanded={expanded === 'screening'} onChange={handleChange('screening')} sx={{
          borderRadius: 3,
          mb: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-628">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-630" />} data-id="element-629">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-631">
                <Avatar sx={{
                bgcolor: 'warning.light',
                width: 32,
                height: 32
              }} data-id="element-632">
                  <SecurityIcon fontSize="small" data-id="element-633" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-634">
                  Screening Summary
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-635">
              <Grid container spacing={2} data-id="element-636">
                <Grid item xs={12} data-id="element-637">
                  <Typography variant="caption" color="text.secondary" data-id="element-638">
                    Screening Status
                  </Typography>
                  <Box data-id="element-639">
                    <Chip label={transactionData?.screening.status} color={transactionData?.screening.status === 'Passed' ? 'success' : transactionData?.screening.status === 'Pending' ? 'warning' : 'error'} size="small" data-id="element-640" />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Timeline Accordion */}
          <Accordion expanded={expanded === 'timeline'} onChange={handleChange('timeline')} sx={{
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }} data-id="element-641">
            <AccordionSummary expandIcon={<ExpandMoreIcon data-id="element-643" />} data-id="element-642">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-644">
                <Avatar sx={{
                bgcolor: 'error.light',
                width: 32,
                height: 32
              }} data-id="element-645">
                  <TimelineIcon fontSize="small" data-id="element-646" />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} data-id="element-647">
                  Timeline
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-id="element-648">
              <Box sx={{
              position: 'relative',
              pl: 3
            }} data-id="element-649">
                {transactionData.timeline.map((item, index) => <Box key={index} sx={{
                display: 'flex',
                mb: index < transactionData.timeline.length - 1 ? 3 : 0,
                position: 'relative'
              }} data-id="element-650">
                    {/* Timeline connector line */}
                    {index < transactionData.timeline.length - 1 && <Box sx={{
                  position: 'absolute',
                  left: 16,
                  top: 32,
                  bottom: -24,
                  width: 2,
                  bgcolor: item.status === 'completed' ? 'success.main' : item.status === 'current' ? 'primary.main' : item.status === 'error' ? 'error.main' : 'grey.300'
                }} data-id="element-651" />}
                    {/* Timeline dot */}
                    <Box sx={{
                  position: 'absolute',
                  left: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-652">
                      <Avatar sx={{
                    width: 32,
                    height: 32,
                    bgcolor: item.status === 'completed' ? 'success.main' : item.status === 'current' ? 'primary.main' : item.status === 'error' ? 'error.main' : 'grey.400'
                  }} data-id="element-653">
                        {item.status === 'completed' ? <CheckCircleIcon sx={{
                      fontSize: 18
                    }} data-id="element-654" /> : <HourglassEmptyIcon sx={{
                      fontSize: 18
                    }} data-id="element-655" />}
                      </Avatar>
                    </Box>
                    {/* Timeline content */}
                    <Box sx={{
                  ml: 5,
                  flex: 1
                }} data-id="element-656">
                      <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 0.5
                  }} data-id="element-657">
                        <Typography variant="subtitle2" fontWeight={600} data-id="element-658">
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{
                      ml: 2
                    }} data-id="element-659">
                          {item.timestamp}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" data-id="element-660">
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
    </Dialog>;
};
export default AccordionTransactionModal;